// Token-derived permissions for the admin UI.
//
// Loaded ONCE after login from `/api/me` (effective permission tokens + role keys) plus the
// static `/api/permissions/catalog`. Everything downstream : the dynamic sidebar, route guards
// and write-gating : is a synchronous `Set.has` against these tokens, so the UI never spams the
// API to check a permission. Goes through the shared `axios` instance so the Auth0AxiosBridge
// attaches the admin Bearer token (raw fetch would be unauthenticated). Under bypass-auth the
// same `/api/me` returns the full super-admin permission set, so no client-side special-casing.
import axios from "axios";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

export interface CatalogFeature {
	key: string;
	label: string;
	section: string;
	supportsWrite: boolean;
	/** True pour les rares fonctionnalités à action « purge » (ex. deleted-pages). Absent = false. */
	supportsPurge?: boolean;
}
export interface CatalogGroup {
	key: string;
	label: string;
	typeKeys: string[];
}
export interface CatalogType {
	key: string;
	label: string;
	groupKey: string;
}
export interface PermissionCatalog {
	features: CatalogFeature[];
	groups: CatalogGroup[];
	contentTypes: CatalogType[];
}

/** The current admin's identity, from /api/me : used e.g. to recognise one's own realtime events. */
export interface Identity {
	name?: string | null;
	email?: string | null;
	sub?: string | null;
}

/** Badge de rôle du compte connecté, calculé côté serveur (rôles MAXIMAUX seulement). */
export interface OwnRoleBadge {
	roleKey: string;
	label: string;
	color: string;
	kind: string;
}

export interface PermissionsState {
	ready: boolean;
	roles: string[];
	permissions: Set<string>;
	/** Badges des rôles maximaux du compte connecté (dominance serveur, internal jamais listé). */
	badges: OwnRoleBadge[];
	catalog: PermissionCatalog | null;
	/** The signed-in admin's identity (name/email/sub). */
	identity: Identity;
	/** True when `user` (a name/email/sub string, e.g. from an audit or realtime event) is the current admin. */
	isMe: (user?: string | null) => boolean;
	/** True if the principal holds the exact permission token. */
	can: (token: string) => boolean;
	canFeatureRead: (featureKey: string) => boolean;
	canFeatureWrite: (featureKey: string) => boolean;
	canContentWrite: (typeKey: string) => boolean;
	/** Peut déployer ce type en preview (verbe publish ; promote l'implique). */
	canContentPublish: (typeKey: string) => boolean;
	/** Peut mettre ce type en production (publier ET dépublier la production). */
	canContentPromote: (typeKey: string) => boolean;
	/** True if the current admin may see/enter the given admin route. */
	routeAllowed: (pathname: string) => boolean;
	/**
	 * Le chargement des autorisations a échoué (réseau, serveur en cours de redéploiement).
	 * À NE PAS confondre avec « aucune permission » : dans ce cas l'ensemble est vide et tout
	 * serait refusé, mais la cause est le transport, pas le compte. L'interface doit le dire.
	 */
	loadFailed: boolean;
	reload: () => void;
}

const PermissionsContext = createContext<PermissionsState | null>(null);

// Each admin route prefix → the feature whose READ permission it requires. Longest-prefix wins,
// so `/content/<id>` and `/page/<id>` both map to `content`. Routes not listed are always allowed.
const ROUTE_FEATURE: Array<[string, string]> = [
	["/dashboard", "dashboard"],
	["/analytics", "analytics"],
	["/metrics", "metrics"],
	["/logs", "logs"],
	// La page Audit lit le même journal que /logs : même permission de lecture.
	["/audit", "logs"],
	["/content", "content"],
	["/pages", "content"],
	["/new-page", "content"],
	["/page", "content"],
	["/connections", "architecture"],
	["/layout", "layout"],
	["/design-library", "design-library"],
	["/languages", "languages"],
	["/traduction", "traduction"],
	["/content-types", "content-types"],
	["/taxonomie", "taxonomie"],
	["/categories", "categories"],
	["/search-tags", "search-tags"],
	["/fragments", "fragments"],
	["/external-api", "external-api"],
	["/contact-info", "contact-info"],
	["/redirections", "redirections"],
	["/sitemaps", "sitemaps"],
	["/storage", "storage"],
	["/permissions", "roles"],
	["/network-status", "network-status"],
	["/exchange-rates", "exchange-rates"],
	["/macro-data", "macro-data"],
	["/newsletters", "newsletters"],
	["/contact-messages", "contact-messages"],
	["/recrutement", "recrutement"],
	["/tickets", "tickets"],
	["/users", "users"],
	["/backup", "backup"],
	["/deployment", "deployment"],
	// Réservée au palier admin : la feature est exemptée du wildcard read côté serveur.
	["/search-metrics", "search-metrics"],
	["/deleted-pages", "deleted-pages"],
	["/settings", "settings"]
];

/**
 * Route d'atterrissage après login, dérivée des permissions. Le Dashboard reste la porte
 * d'entrée par défaut, SAUF pour les rôles mono-métier : quand les features lisibles hors
 * dashboard se réduisent à `macro-data` (rôle « macroéconomie », dashboard hérité de
 * bcc-internal), l'agent atterrit directement sur sa page de travail.
 */
export function resolveLandingRoute(permissions: Set<string>): string {
	const readable = new Set<string>();
	for (const token of Array.from(permissions)) {
		const match = /^feature:([a-z0-9-]+):(read|write)$/.exec(token);
		if (match) readable.add(match[1]);
	}
	const nonDashboard = Array.from(readable).filter(feature => feature !== "dashboard");
	if (nonDashboard.length > 0 && nonDashboard.every(feature => feature === "macro-data" || feature === "macro-publish")) {
		return "/macro-data";
	}
	if (readable.has("dashboard") || readable.size === 0) {
		return "/dashboard";
	}
	// Pas de dashboard : première route autorisée dans l'ordre de la table.
	for (const [route, feature] of ROUTE_FEATURE) {
		if (readable.has(feature)) return route;
	}
	return "/dashboard";
}

/** The feature a route maps to (longest-prefix match), or null when the route is unrestricted. */
export function featureForRoute(pathname: string): string | null {
	let best: string | null = null;
	let bestLen = -1;
	for (const [route, feature] of ROUTE_FEATURE) {
		if ((pathname === route || pathname.startsWith(route + "/")) && route.length > bestLen) {
			best = feature;
			bestLen = route.length;
		}
	}
	return best;
}

export function PermissionsProvider({ children }: { children: React.ReactNode }): React.ReactElement {
	const [ready, setReady] = useState(false);
	const [roles, setRoles] = useState<string[]>([]);
	const [permissions, setPermissions] = useState<Set<string>>(new Set());
	const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
	const [identity, setIdentity] = useState<Identity>({});
	const [badges, setBadges] = useState<OwnRoleBadge[]>([]);
	const [loadFailed, setLoadFailed] = useState(false);

	const load = useCallback(async () => {
		try {
			const [me, cat] = await Promise.all([
				axios.get(`${apiBase}/api/me`, { withCredentials: true }),
				axios.get<PermissionCatalog>(`${apiBase}/api/permissions/catalog`, { withCredentials: true })
			]);
			setRoles(Array.isArray(me.data?.roles) ? me.data.roles : []);
			setPermissions(new Set<string>(Array.isArray(me.data?.permissions) ? me.data.permissions : []));
			setIdentity({ name: me.data?.name ?? null, email: me.data?.email ?? null, sub: me.data?.sub ?? null });
			setBadges(Array.isArray(me.data?.badges) ? me.data.badges : []);
			setCatalog(cat.data ?? null);
			setLoadFailed(false);
		} catch {
			// Real error (network/401) → lock down rather than leak. Bypass-auth never lands here.
			// Mais on RETIENT que c'est un échec de chargement : sans ce drapeau, un poste coupé du
			// serveur voit « Accès refusé » partout et croit son compte révoqué.
			setPermissions(new Set());
			setLoadFailed(true);
		} finally {
			setReady(true);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const value = useMemo<PermissionsState>(() => {
		const can = (token: string) => permissions.has(token);
		const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();
		const mine = new Set([norm(identity.name), norm(identity.email), norm(identity.sub)].filter(Boolean));
		return {
			ready,
			roles,
			permissions,
			badges,
			catalog,
			identity,
			isMe: (user) => {
				const u = norm(user);
				return u.length > 0 && mine.has(u);
			},
			can,
			canFeatureRead: (k) => can(`feature:${k}:read`),
			canFeatureWrite: (k) => can(`feature:${k}:write`),
			canContentWrite: (k) => can(`content:${k}:write`),
			canContentPublish: (k) => can(`content:${k}:publish`),
			canContentPromote: (k) => can(`content:${k}:promote`),
			routeAllowed: (pathname) => {
				const feature = featureForRoute(pathname);
				return feature == null ? true : can(`feature:${feature}:read`);
			},
			loadFailed,
			reload: () => void load()
		};
	}, [ready, roles, permissions, badges, catalog, identity, loadFailed, load]);

	if (!ready) {
		return (
			<div className="flex h-screen items-center justify-center bg-slate-50">
				<div className="flex flex-col items-center gap-3 text-slate-500">
					<div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
					<p className="text-sm font-medium">Chargement des autorisations…</p>
				</div>
			</div>
		);
	}

	return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

const FALLBACK: PermissionsState = {
	ready: true,
	roles: [],
	permissions: new Set(),
	catalog: null,
	identity: {},
	isMe: () => false,
	can: () => false,
	canFeatureRead: () => true,
	canFeatureWrite: () => false,
	canContentWrite: () => false,
	canContentPublish: () => false,
	canContentPromote: () => false,
	badges: [],
	routeAllowed: () => true,
	loadFailed: false,
	reload: () => undefined
};

/** Hook for functional components. Outside the provider it returns a permissive-read/no-write
 * fallback so a stray consumer never crashes the app. */
export function usePermissions(): PermissionsState {
	return useContext(PermissionsContext) ?? FALLBACK;
}

/** Class components read this directly via `static contextType`. */
export { PermissionsContext };

/**
 * Blocks a route the current admin lacks read access to, rendering a friendly notice.
 * Distingue deux causes que l'ancienne version confondait : autorisations JAMAIS CHARGÉES
 * (transport) et autorisations chargées mais insuffisantes (droits).
 */
export function RouteGuard({ children }: { children: React.ReactNode }): React.ReactElement {
	const { routeAllowed, loadFailed, reload } = usePermissions();
	const location = useLocation();
	if (loadFailed) {
		return (
			<div className="flex flex-1 items-center justify-center p-10">
				<div className="max-w-md rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center shadow-sm">
					<p className="text-lg font-semibold text-amber-900">Autorisations indisponibles</p>
					<p className="mt-2 text-sm text-amber-800">
						Vos droits n'ont pas pu être chargés : le serveur d'administration n'a pas répondu. Votre
						compte n'est pas en cause. Vérifiez votre connexion, puis réessayez.
					</p>
					<button
						type="button"
						className="mt-5 rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
						onClick={() => reload()}
					>
						Réessayer
					</button>
				</div>
			</div>
		);
	}
	if (!routeAllowed(location.pathname)) {
		return (
			<div className="flex flex-1 items-center justify-center p-10">
				<div className="max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
					<p className="text-lg font-semibold text-slate-800">Accès refusé</p>
					<p className="mt-2 text-sm text-slate-500">
						Vous n'avez pas l'autorisation d'accéder à cette page. Contactez un administrateur si vous pensez
						qu'il s'agit d'une erreur.
					</p>
				</div>
			</div>
		);
	}
	return <>{children}</>;
}
